import type { ActionFunctionArgs, LinksFunction, LoaderFunctionArgs } from "@remix-run/cloudflare";
import { json } from "@remix-run/cloudflare";
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	isRouteErrorResponse,
	useLoaderData,
	useRouteError,
	useRouteLoaderData,
} from "@remix-run/react";
import * as Sentry from "@sentry/browser";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpLink } from "@trpc/client";
import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Button, ButtonLink, Empty } from "./components/ui";
import { GlobalUploadDrop } from "./components/upload/GlobalUploadDrop";
import styles from "./globals.css?url";
import { createTranslator, defaultLocale, getMessages, isLocale, localeHtmlLang } from "./i18n";
import { I18nProvider } from "./i18n/context";
import { getLocaleFromRequest, localeCookieHeader } from "./i18n/server";
import { shouldCaptureException } from "./lib/sentry-filters";

export async function loader({ request }: LoaderFunctionArgs) {
	const locale = getLocaleFromRequest(request);

	return json({ locale }, { headers: { "Set-Cookie": localeCookieHeader(locale) } });
}

/** Bots/forms POSTing to `/` — avoid Remix "no action on root" errors in Sentry. */
export async function action(_args: ActionFunctionArgs) {
	return json({ error: "Method not allowed" }, { status: 405 });
}

export const links: LinksFunction = () => [
	{ rel: "stylesheet", href: styles },
	{
		rel: "stylesheet",
		href: "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700&display=swap",
	},
];

export function Layout({ children }: { children: React.ReactNode }) {
	const rootData = useRouteLoaderData<typeof loader>("root");
	const locale = isLocale(rootData?.locale) ? rootData.locale : defaultLocale;

	return (
		<html lang={localeHtmlLang(locale)}>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
			</head>
			<body className="bg-background font-sans text-foreground antialiased">
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

export const ErrorBoundary = () => {
	const error = useRouteError();
	const rootData = useRouteLoaderData<{ locale: "ja" | "en" }>("root");
	const locale = isLocale(rootData?.locale) ? rootData.locale : defaultLocale;
	const t = createTranslator(getMessages(locale));

	if (shouldCaptureException(error)) {
		Sentry.captureException(error, {
			tags: {
				location: "error_boundary",
				error_type: error instanceof Error ? error.name : "Unknown",
			},
		});
	}

	const notFound = isRouteErrorResponse(error) && error.status === 404;

	return (
		<main className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
			<Empty
				title={notFound ? t("notFound.title") : t("error.title")}
				description={notFound ? t("notFound.description") : t("error.recovery")}
				action={
					<>
						{!notFound && <Button onClick={() => window.location.reload()}>{t("error.reload")}</Button>}
						<ButtonLink variant={notFound ? "primary" : "secondary"} href="/">
							{t("notFound.backHome")}
						</ButtonLink>
					</>
				}
			/>
		</main>
	);
};

export default function App() {
	const { locale } = useLoaderData<typeof loader>();
	const [queryClient] = useState(() => new QueryClient());
	const [trpcClient] = useState(() =>
		trpc.createClient({
			links: [
				httpLink({
					url: import.meta.env.VITE_TRPC_URL || "http://localhost:5173/share/trpc",
					fetch(url, options) {
						return fetch(url, {
							...options,
							credentials: "include",
						});
					},
				}),
			],
		}),
	);
	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<I18nProvider locale={locale}>
					<GlobalUploadDrop>
						<Outlet />
					</GlobalUploadDrop>
				</I18nProvider>
			</QueryClientProvider>
		</trpc.Provider>
	);
}
